import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { DeleteMembershipPlanResponse } from '../types';

interface UseDeleteMembershipPlanOptions {
  onSuccess?: (result: DeleteMembershipPlanResponse) => void;
  onError?: (error: Error) => void;
  /** Off for bulk deletes, which raise one summary toast instead. */
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export const useDeleteMembershipPlan = (
  options?: UseDeleteMembershipPlanOptions
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (planId: string) =>
      apiClient.delete<DeleteMembershipPlanResponse>(
        `membership-plans/${planId}`
      ),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['membership-plans'] });
      if (options?.showSuccessToast !== false) {
        toast.success(
          result.deactivated
            ? 'Plan has sold memberships, so it was deactivated instead of deleted'
            : 'Membership plan deleted'
        );
      }
      options?.onSuccess?.(result);
    },
    onError: (error: Error) => {
      if (options?.showErrorToast !== false) {
        toast.error(error.message || 'Failed to delete membership plan');
      }
      options?.onError?.(error);
    },
  });

  return {
    deleteMembershipPlan: mutation.mutate,
    deleteMembershipPlanAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
  };
};
